import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PremiumGate } from '@/components/PremiumGate'
import { Spinner } from '@/components/Spinner'
import * as v3 from '@/api/v3'
import * as v5 from '@/api/v5'
import type { V5AddressSummary } from '@/types/api'
import { addressLabel } from '@/lib/addresses'

function SolarGuardToggle({ addressUuid }: { addressUuid: string }) {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setEnabled(null)
    setLoadError(false)
    v3.getSolarGuard(addressUuid, controller.signal)
      .then((state) => setEnabled(state.enabled))
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true)
      })
    return () => controller.abort()
  }, [addressUuid])

  async function toggle() {
    if (enabled === null || saving) return
    const next = !enabled
    setSaving(true)
    setSaveError('')
    try {
      await v3.setSolarGuard(addressUuid, next)
      setEnabled(next)
    } catch {
      setSaveError('Opslaan is niet gelukt. Probeer het opnieuw.')
    } finally {
      setSaving(false)
    }
  }

  if (loadError) {
    return (
      <div className="card-beige p-5 text-base font-semibold text-red md:p-[30px]">
        We konden de Solar Guard-status van dit adres niet ophalen. Probeer het later opnieuw.
      </div>
    )
  }

  if (enabled === null) {
    return (
      <div className="flex items-center gap-3 py-10 text-base text-dark-blue/60">
        <Spinner /> Solar Guard-status ophalen…
      </div>
    )
  }

  return (
    <div className="card-beige p-5 md:p-[30px]">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-xl font-semibold">Deelname Solar Guard</h3>
          <p className="mt-1 max-w-xl text-base leading-normal text-dark-blue/60">
            {enabled
              ? 'Solar Guard staat aan. We bewaken je zonnepanelen op uitval door netcongestie en delen telemetrie met de netbeheerder, zodat jouw huishouden helpt congestie te voorkomen.'
              : 'Solar Guard staat uit. We bewaken je zonnepanelen niet op uitval en delen geen telemetrie met de netbeheerder.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Deelname Solar Guard"
          disabled={saving}
          onClick={toggle}
          className={`relative h-9 w-16 shrink-0 rounded-full transition-colors duration-300 disabled:opacity-60 ${
            enabled ? 'bg-green' : 'bg-beige-2'
          }`}
        >
          <span
            className={`absolute top-1 flex size-7 items-center justify-center rounded-full bg-white shadow-sm transition-all duration-300 ${
              enabled ? 'left-8' : 'left-1'
            }`}
          >
            {saving && <Spinner className="size-4" />}
          </span>
        </button>
      </div>
      {saveError && <p className="mt-3 text-base font-semibold text-red">{saveError}</p>}
      <p className="mt-4 border-t border-beige-2 pt-4 text-15 leading-normal text-dark-blue/60">
        Solar Guard werkt zodra je zonne-omvormer is gekoppeld in de easyEnergy-app. Telemetrie
        wordt alleen gedeeld met netbeheerders zolang je deelneemt; je kunt je deelname op elk
        moment aan- of uitzetten.
      </p>
    </div>
  )
}

export function SolarGuardPage() {
  const [addresses, setAddresses] = useState<V5AddressSummary[] | null>(null)
  const [selectedUuid, setSelectedUuid] = useState<string>('')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    v5.getAddresses()
      .then((list) => {
        if (cancelled) return
        setAddresses(list)
        if (list.length === 1) setSelectedUuid(list[0].addressUuid)
      })
      .catch(() => {
        if (!cancelled) setError('We konden je adressen niet ophalen. Probeer het later opnieuw.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selected = useMemo(
    () => addresses?.find((a) => a.addressUuid === selectedUuid) ?? null,
    [addresses, selectedUuid]
  )

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-12">
      <nav className="mb-5 text-15 text-dark-blue/60">
        <Link to="/services" className="transition-all duration-300 hover:text-dark-blue">
          Diensten
        </Link>{' '}
        <span className="mx-1">/</span>
        <span className="text-dark-blue">Solar Guard</span>
      </nav>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <h1 className="max-w-md text-28 font-semibold leading-111 lg:text-44">Solar Guard</h1>
        <p className="max-w-96 text-base leading-6 text-dark-blue">
          Solar Guard bewaakt je zonnepanelen op uitval door netcongestie. Doe je mee, dan delen
          we telemetrie van je installatie met netbeheerders — zo help jij als huishouden
          netcongestie te voorkomen.
        </p>
      </div>

      <div className="mt-8">
        <PremiumGate tier="basis">
          {error && (
            <div className="card-beige p-5 text-base font-semibold text-red md:p-[30px]">
              {error}
            </div>
          )}

          {!error && addresses === null && (
            <div className="flex items-center gap-3 py-10 text-base text-dark-blue/60">
              <Spinner /> Adressen laden…
            </div>
          )}

          {addresses !== null && addresses.length === 0 && (
            <div className="card-beige p-5 text-base leading-normal text-dark-blue/60 md:p-[30px]">
              Er zijn nog geen adressen gekoppeld aan je account. Voeg je adres toe via de
              easyEnergy-app en kom daarna terug.
            </div>
          )}

          {addresses !== null && addresses.length > 1 && (
            <label className="mb-5 flex max-w-md flex-col gap-1">
              <span className="field-label">Adres</span>
              <select
                className="input"
                value={selectedUuid}
                onChange={(e) => setSelectedUuid(e.target.value)}
              >
                <option value="" disabled>
                  Kies een adres…
                </option>
                {addresses.map((a) => (
                  <option key={a.addressUuid} value={a.addressUuid}>
                    {addressLabel(a)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {selected && <SolarGuardToggle key={selected.addressUuid} addressUuid={selected.addressUuid} />}
        </PremiumGate>
      </div>
    </div>
  )
}
